import type { MatchState, UseAbilityCommand } from "../../../../schemas"
import type { CommandResult } from "../state"
import {
  appendLog,
  applyTemporaryModifier,
  countOtherAllies,
  drawCards,
  err,
  getOtherSeatId,
  getSeat,
  moveInstance,
  updateInstance,
  updateSeat,
} from "../state"

export function handleUseAbility(
  state: MatchState,
  cmd: UseAbilityCommand,
): CommandResult {
  const seat = getSeat(state, cmd.seatId)
  const { sourceId, skillId } = cmd.payload

  const source = state.instances[sourceId]
  if (!source)
    return err(
      "USE_ABILITY_UNKNOWN_SOURCE",
      `Unknown source instance ${sourceId}.`,
    )

  // Source must be in a zone owned by the active player
  const ownerZones = [seat.zones.active, ...seat.zones.support]
  if (!ownerZones.includes(sourceId)) {
    return err(
      "USE_ABILITY_NOT_OWNED",
      "Source must be a unit in play that you control.",
    )
  }

  const mode = resolveMode(cmd)
  const allyRequirement = cmd.payload.allyRequirement

  if (mode !== "non_exhausting" && source.status.exhausted) {
    return err(
      "USE_ABILITY_EXHAUSTED",
      "Source is exhausted and cannot use this ability.",
    )
  }

  if (
    source.status.jammedForTurnNumber === state.turn.number &&
    mode !== "non_exhausting"
  ) {
    return err(
      "USE_ABILITY_JAMMED",
      "This unit is jammed and cannot use exhausting abilities this turn.",
    )
  }

  if (
    allyRequirement &&
    countOtherAllies(state, cmd.seatId, sourceId) < allyRequirement
  ) {
    return err(
      "USE_ABILITY_ALLY_REQUIREMENT",
      `This ability requires ${allyRequirement} other allied unit${allyRequirement === 1 ? "" : "s"} in play.`,
    )
  }

  // Execute the ability effect
  const effectResult = executeSkillEffect(state, cmd, skillId)
  if (!effectResult.ok) return effectResult
  let s = effectResult.state

  // Apply mode consequences
  if (mode === "default") {
    s = updateInstance(s, sourceId, (inst) => ({
      ...inst,
      status: { ...inst.status, exhausted: true },
      memory: {
        ...inst.memory,
        timesActivated: inst.memory.timesActivated + 1,
      },
    }))
  } else if (mode === "non_exhausting") {
    s = updateInstance(s, sourceId, (inst) => ({
      ...inst,
      memory: {
        ...inst.memory,
        timesActivated: inst.memory.timesActivated + 1,
      },
    }))
  } else if (mode === "self_destruct") {
    s = updateInstance(s, sourceId, (inst) => ({
      ...inst,
      memory: {
        ...inst.memory,
        timesActivated: inst.memory.timesActivated + 1,
      },
    }))
    s = moveInstance(s, sourceId, "scrap", cmd.seatId)
  }

  s = appendLog(s, cmd.seatId, "use_ability", { sourceId, skillId, mode })

  return { ok: true, state: s }
}

function resolveMode(
  cmd: UseAbilityCommand,
): "default" | "non_exhausting" | "self_destruct" {
  return cmd.payload.mode ?? "default"
}

type SkillEffectResult =
  | { ok: true; state: MatchState }
  | { ok: false; code: string; message: string }

function executeSkillEffect(
  state: MatchState,
  cmd: UseAbilityCommand,
  skillId: string,
): SkillEffectResult {
  switch (skillId) {
    case "repair":
      return applyRepair(state, cmd)
    case "rally":
      return applyRally(state, cmd)
    case "scout":
      return { ok: true, state: drawCards(state, cmd.seatId, 1) }
    case "battery":
      return applyBattery(state, cmd)
    case "broker":
      return applyBroker(state, cmd)
    case "jam":
      return applyJam(state, cmd)
    case "shift":
      return applyShift(state, cmd)
    case "recover":
      return applyRecover(state, cmd)
    case "shield":
      return applyShield(state, cmd)
    case "salvage":
      return applySalvage(state, cmd)
    case "scrap":
      return applyScrap(state, cmd)
    case "recycle":
      return applyRecycle(state, cmd)
    // Passives / triggered effects — not directly activated
    case "guard":
    case "swift":
    case "sneak":
    case "armor":
    case "breakthrough":
    case "ranged":
    case "ko":
    case "focus":
    case "coordinated_strike":
    case "deploy":
    case "spark":
    case "charge":
    case "generator":
      return err(
        "USE_ABILITY_NOT_ACTIVATED",
        `Skill "${skillId}" is passive/keyword and cannot be directly activated.`,
      )
    default:
      return err("USE_ABILITY_UNKNOWN_SKILL", `Unknown skill "${skillId}".`)
  }
}

function applyRepair(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const sourceId = cmd.payload.sourceId
  return {
    ok: true,
    state: applyTemporaryModifier(state, sourceId, {
      healthMod: 2,
      expiresAfterTurn: getOpponentNextTurnNumber(state),
      expiresAfterSeatId: getOtherSeatId(cmd.seatId),
    }),
  }
}

function applyRally(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  return {
    ok: true,
    state: applyTemporaryModifier(state, cmd.payload.sourceId, {
      attackMod: 1,
      expiresAfterTurn: state.turn.number,
    }),
  }
}

function applyBroker(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  // Proxy: add 1 energy to represent cost reduction
  const updated = updateSeat(state, cmd.seatId, (s) => ({
    ...s,
    resources: { ...s.resources, currentEnergy: s.resources.currentEnergy + 1 },
  }))
  return { ok: true, state: updated }
}

function applyJam(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const targetId = (cmd.payload.target as Record<string, unknown> | undefined)
    ?.instanceId as string | undefined
  if (!targetId)
    return err(
      "JAM_NO_TARGET",
      "Jam requires a target unit instanceId in payload.target.",
    )
  const inst = state.instances[targetId]
  if (!inst) return err("JAM_UNKNOWN_TARGET", `Unknown target ${targetId}.`)
  const jammedForTurnNumber = getNextOwnerTurnNumber(state, inst.ownerSeatId)
  const s = updateInstance(state, targetId, (i) => ({
    ...i,
    status: {
      ...i.status,
      jammedForTurnNumber,
      canAttackThisTurn:
        inst.ownerSeatId === state.activeSeat
          ? false
          : i.status.canAttackThisTurn,
      remainingAttack:
        inst.ownerSeatId === state.activeSeat ? 0 : i.status.remainingAttack,
    },
  }))
  return { ok: true, state: s }
}

function applyShift(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const sourceId = cmd.payload.sourceId
  const seat = getSeat(state, cmd.seatId)
  if (!seat.zones.support.includes(sourceId)) {
    return err("SHIFT_NOT_IN_SUPPORT", "Shift source must be in support.")
  }
  if (seat.zones.active === null) {
    return err("SHIFT_NO_ACTIVE", "No active unit to swap with.")
  }
  const currentActiveId = seat.zones.active
  let s = moveInstance(state, currentActiveId, "support", cmd.seatId)
  s = updateInstance(s, currentActiveId, (i) => ({
    ...i,
    status: { ...i.status, canAttackThisTurn: false, remainingAttack: 0 },
  }))
  s = moveInstance(s, sourceId, "active", cmd.seatId)
  const shiftedInst = s.instances[sourceId]
  if (shiftedInst) {
    const canAttack =
      shiftedInst.combatState.isSwift && !shiftedInst.status.hasAttackedThisTurn
    s = updateInstance(s, sourceId, (i) => ({
      ...i,
      status: {
        ...i.status,
        canAttackThisTurn: canAttack,
        remainingAttack: canAttack ? i.currentAttack : 0,
      },
    }))
  }
  return { ok: true, state: s }
}

function applyRecover(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const seat = getSeat(state, cmd.seatId)
  const targetId = seat.zones.active ?? cmd.payload.sourceId
  const s = updateInstance(state, targetId, (i) => ({
    ...i,
    status: {
      ...i.status,
      exhausted: false,
      canAttackThisTurn:
        !i.status.hasAttackedThisTurn &&
        (i.zone === "active" ||
          (i.zone === "support" && i.combatState.hasRanged)),
      remainingAttack:
        !i.status.hasAttackedThisTurn &&
        (i.zone === "active" ||
          (i.zone === "support" && i.combatState.hasRanged))
          ? i.currentAttack
          : 0,
    },
  }))
  return { ok: true, state: s }
}

function applyShield(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const seat = getSeat(state, cmd.seatId)
  const targetId = seat.zones.active ?? cmd.payload.sourceId
  return {
    ok: true,
    state: applyTemporaryModifier(state, targetId, {
      healthMod: 1,
      expiresAfterTurn: getOpponentNextTurnNumber(state),
      expiresAfterSeatId: getOtherSeatId(cmd.seatId),
    }),
  }
}

function applySalvage(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const seat = getSeat(state, cmd.seatId)
  const topDiscardId = seat.zones.discard[seat.zones.discard.length - 1]
  if (!topDiscardId) {
    return err(
      "SALVAGE_NO_DISCARD",
      "Salvage requires at least one card in your discard pile.",
    )
  }

  return {
    ok: true,
    state: moveInstance(state, topDiscardId, "hand", cmd.seatId),
  }
}

function applyScrap(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const withDraw = drawCards(state, cmd.seatId, 1)
  return {
    ok: true,
    state: moveInstance(withDraw, cmd.payload.sourceId, "scrap", cmd.seatId),
  }
}

function applyBattery(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const amount = (cmd.payload.target as { amount?: unknown } | undefined)
    ?.amount
  if (typeof amount !== "number" || !Number.isInteger(amount) || amount < 1) {
    return err(
      "BATTERY_INVALID_AMOUNT",
      "Battery requires a positive integer amount in payload.target.amount.",
    )
  }
  const seat = getSeat(state, cmd.seatId)
  if (seat.resources.currentEnergy < amount) {
    return err(
      "BATTERY_INSUFFICIENT_ENERGY",
      `Battery requires ${amount} energy but you only have ${seat.resources.currentEnergy}.`,
    )
  }
  let s = updateSeat(state, cmd.seatId, (seat) => ({
    ...seat,
    resources: {
      ...seat.resources,
      currentEnergy: seat.resources.currentEnergy - amount,
    },
  }))
  s = updateInstance(s, cmd.payload.sourceId, (inst) => ({
    ...inst,
    pendingBatteryEnergy: amount,
  }))
  return { ok: true, state: s }
}

function applyRecycle(
  state: MatchState,
  cmd: UseAbilityCommand,
): SkillEffectResult {
  const targetId = (cmd.payload.target as Record<string, unknown> | undefined)
    ?.instanceId as string | undefined
  if (!targetId) {
    return err(
      "RECYCLE_NO_TARGET",
      "Recycle requires one of your cards in hand or play as the target.",
    )
  }
  const seat = getSeat(state, cmd.seatId)
  const legalTargets = new Set([
    ...seat.zones.hand,
    ...(seat.zones.active ? [seat.zones.active] : []),
    ...seat.zones.support,
  ])
  if (!legalTargets.has(targetId)) {
    return err(
      "RECYCLE_INVALID_TARGET",
      "Recycle can only target one of your cards in hand or play.",
    )
  }
  const withDraw = drawCards(state, cmd.seatId, 2)
  return {
    ok: true,
    state: moveInstance(withDraw, targetId, "discard", cmd.seatId),
  }
}

function getNextOwnerTurnNumber(
  state: MatchState,
  ownerSeatId: "seat_a" | "seat_b",
): number {
  if (ownerSeatId === state.activeSeat) {
    return state.turn.number + 1
  }
  return ownerSeatId === "seat_b" ? state.turn.number : state.turn.number + 1
}

function getOpponentNextTurnNumber(state: MatchState): number {
  return state.activeSeat === "seat_a"
    ? state.turn.number
    : state.turn.number + 1
}
